/**
 * Custom cursor with magnetic effect
 * Disabled on touch devices
 */

import { RAFLoop, isTouchDevice, lerp } from '../core/utils.js';

export class CustomCursor {
  #cursor;
  #cursorDot;
  #rafLoop;
  #position = { x: 0, y: 0, targetX: 0, targetY: 0 };
  #dotPosition = { x: 0, y: 0 };
  #isHovering = false;
  #isVisible = false;
  #magneticElements = new WeakSet();
  #abortController = new AbortController();

  constructor() {
    if (isTouchDevice()) {
      this.#disable();
      return;
    }

    this.#cursor = document.getElementById('cursor');
    this.#cursorDot = document.getElementById('cursorDot');

    if (!this.#cursor || !this.#cursorDot) {
      console.warn('[CustomCursor] Elements not found');
      return;
    }

    this.#init();
  }

  #init() {
    const signal = this.#abortController.signal;

    // Mouse tracking
    document.addEventListener('mousemove', (e) => {
      this.#position.targetX = e.clientX;
      this.#position.targetY = e.clientY;
      
      if (!this.#isVisible) {
        this.#isVisible = true;
        this.#cursor.style.opacity = '1';
        this.#cursorDot.style.opacity = '1';
      }
    }, { passive: true, signal });

    // Hover effects
    const hoverElements = document.querySelectorAll('a, button, [role="button"], .project-card, .magnetic');
    
    hoverElements.forEach(el => {
      el.addEventListener('mouseenter', () => {
        this.#isHovering = true;
        this.#cursor.classList.add('hover');
      }, { signal });
      
      el.addEventListener('mouseleave', () => {
        this.#isHovering = false;
        this.#cursor.classList.remove('hover');
        el.style.transform = '';
      }, { signal });

      // Click effect
      el.addEventListener('mousedown', () => {
        this.#cursor.classList.add('clicking');
      }, { signal });
      
      el.addEventListener('mouseup', () => {
        this.#cursor.classList.remove('clicking');
      }, { signal });
    });

    // Magnetic effect
    document.querySelectorAll('.magnetic').forEach(el => {
      this.#magneticElements.add(el);
      
      el.addEventListener('mousemove', (e) => {
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        el.style.transform = `translate(${x * 0.3}px, ${y * 0.3}px)`;
      }, { passive: true, signal });
    });

    // RAF loop for smooth animation
    this.#rafLoop = new RAFLoop(() => {
      // Smooth cursor follow
      this.#position.x = lerp(this.#position.x, this.#position.targetX, 0.12);
      this.#position.y = lerp(this.#position.y, this.#position.targetY, 0.12);

      // Outer cursor
      this.#cursor.style.left = `${this.#position.x - 10}px`;
      this.#cursor.style.top = `${this.#position.y - 10}px`;

      // Inner dot follows precisely
      this.#cursorDot.style.left = `${this.#position.targetX - 3}px`;
      this.#cursorDot.style.top = `${this.#position.targetY - 3}px`;
    }, { fps: 60 });

    this.#rafLoop.start();

    // Hide on mouse leave
    document.addEventListener('mouseleave', () => {
      this.#cursor.style.opacity = '0';
      this.#cursorDot.style.opacity = '0';
      this.#isVisible = false;
    }, { signal });
  }

  #disable() {
    // Hide cursor elements on touch devices
    const cursor = document.getElementById('cursor');
    const cursorDot = document.getElementById('cursorDot');
    if (cursor) cursor.style.display = 'none';
    if (cursorDot) cursorDot.style.display = 'none';
  }

  destroy() {
    this.#abortController.abort();
    this.#rafLoop?.stop();
  }
}
